import React, { useEffect, useMemo, useState } from "react";
import { permissions } from "components/auth";
import Dashboard from "components/dashboard";
import { PageSection } from "components/page";
import { QueryResponseHandler } from "components/graphql";
import { externalPlayerEnabled } from "config/features";
import {
  EventAssetTable,
  EventSourcesTable,
  ActionButtons,
  EventData,
  EventActions,
  EventSportData,
  MatchPointConfigData,
  LiveStreamsData,
  EmailReceiptData,
  RecordingsData,
  EventDataTab,
  EmbeddedPlayerTab,
  EvuTemplateTab,
  ComponentStatusTab,
} from "./details-page";
import { useEventDetailsRest } from "./details-page/rest-event-adapter";
import { deleteEventAction } from "../../services/commonService";
import { useAuthDataContext } from "components/auth";
import { parsePage } from "components/table";
import { RestrictedEventVisible, eventStatusTypes } from "pages/event/utils";
import Tabs, { Tab } from "components/tabs";
import { PageHeaderBar } from "components/page";
import { isMobileOnly } from "react-device-detect";
import { DataGridItem } from "components/data-grid";
import { LinkWithIcon } from "components/link";
import { storeTiiviiOttConfigActionCreator } from "../../actions/common-actions";
import { useDispatch } from "react-redux";
import ClipsData from "./details-page/clips-data";
import LocalRecordingsData from "./details-page/local-recordings-data";
import { getAuthorizedRecording } from "../../services/eventServices";
import { useCurrentCustomerContext } from "../../components/current-customer-context";

const camSrcRegex = new RegExp(`^.*\/media[0-9]+\.png$`, "i");

const getFileNameWithoutExtension = filePath =>
  filePath
    .substring(0, filePath.lastIndexOf("."))
    .split("/")
    .pop();

const getAvailableSpaces = (event, intl) => {
  if (!event || !event.infoBert || !event.infoBert.vwsCamList) {
    return [];
  }

  const assetSpaces = event.infoBert.vwsCamList
    .filter(cam => cam.type === "image" && camSrcRegex.test(cam.src))
    .map(({ id, name, src }) => ({
      name: `${intl.formatMessage({ id: "event.asset.slotMixer" })}: ${id} - ${name}`,
      value: getFileNameWithoutExtension(src),
    }));

  return assetSpaces || [];
};

const isSportEvent = event => event && event.type && event.type.isSportEvent;

const hasSportEventData = event => {
  const hasSportData =
    event &&
    event.sportEventData &&
    (event.sportEventData.teamName1Abreviated || event.sportEventData.teamName2Abreviated);
  return isSportEvent(event) && (event.eventStatus !== eventStatusTypes.FINISHED || hasSportData);
};

const showMatchPointConfig = event => {
  const hasMatchPointConfig =
    event &&
    event.matchPointConfig &&
    (event.matchPointConfig.ocurrencesSource ||
      event.matchPointConfig.ocurrencesSourceId ||
      event.matchPointConfig.ocurrencesVisible);
  return isSportEvent(event) && (event.eventStatus !== eventStatusTypes.FINISHED || hasMatchPointConfig);
};

const hasEmailReceipts = event => event && event.emailReceipts && event.emailReceipts.length > 0;
const hasGraphicsetAssets = event =>
  event && event.graphicSet && event.graphicSet.layouts && event.graphicSet.layouts.length > 0;
const isEventFinished = event => event && event.eventStatus && event.eventStatus === eventStatusTypes.FINISHED;
const isEventStarted = event => event && event.eventStatus && event.eventStatus === eventStatusTypes.STARTED;
const isEventDeleted = event => event && event.eventStatus && event.eventStatus === eventStatusTypes.DELETED;

const hasStartedAtLeastOnce = event =>
  event &&
  event.eventStatus &&
  event.eventStatus !== eventStatusTypes.CREATED &&
  event.eventStatus !== eventStatusTypes.SCHEDULED;

const EventDetailsPage = ({
  match: {
    params: { id, customerId },
  },
  location: { search },
  history,
  intl,
}) => {
  const dispatch = useDispatch();
  const { user, hasAdminPrivileges } = useAuthDataContext();
  const { contracts } = useCurrentCustomerContext();

  const { event, loading, error, refreshEvent } = useEventDetailsRest({ eventId: id, withEvu: hasAdminPrivileges });

  const [isAuthorizedRecording, setAuthorizedRecording] = useState(false);

  const availableSpaces = useMemo(() => getAvailableSpaces(event, intl), [event, intl]);

  const contract = contracts?.find(c => c._id === event?.contractId);

  const hasTiiviiOttConfig = contract?.tiivii_ott_config?.enabled ?? false;

  const storeTiiviiOttConfig = data => {
    dispatch(storeTiiviiOttConfigActionCreator(data));
  };

  useEffect(
    () => {
      if (hasTiiviiOttConfig) {
        storeTiiviiOttConfig(hasTiiviiOttConfig);
      }
      return () => storeTiiviiOttConfig(null);
    },
    [hasTiiviiOttConfig],
  );
  const handleSetAuthorizedRecording = response => setAuthorizedRecording(response.record_and_share_available);

  useEffect(
    () => {
      const contractId = event?.contractId;
      if (!!contractId) {
        getAuthorizedRecording(contractId, handleSetAuthorizedRecording);
      }
    },
    [event],
  );

  return (
    <Dashboard>
      <QueryResponseHandler error={error} loading={loading} elements={event}>
        <RestrictedEventVisible event={event}>
          <PageHeaderBar
            showBackButton
            title="app.page.event.title"
            titleParams={{ eventTitle: event ? event.title : "" }}
            buttons={
              <ActionButtons
                event={event}
                id={id}
                customerId={customerId}
                mutation={deleteEventAction}
                history={history}
              />
            }
          />

          {!isMobileOnly && <EventActions event={event} />}

          <PageSection spaced>
            <EventData event={event} customerId={customerId} />
          </PageSection>

          {isMobileOnly && <EventActions event={event} />}

          <PageSection spaced hideBackground>
            <Tabs>
              <Tab label={intl.formatMessage({ id: "event.tab.data" })}>
                <EventDataTab
                  authorizedRecording={event?.authorizedRecording}
                  event={event}
                  showAuthorized={!!event?.localRecInfo && isAuthorizedRecording}
                />
              </Tab>
              {hasAdminPrivileges && hasStartedAtLeastOnce(event) && (
                <Tab label={intl.formatMessage({ id: "event.tab.evuTemplate" })}>
                  <EvuTemplateTab event={event} />
                </Tab>
              )}
              {user.permissions.includes(permissions.event_components_manage) && event?.infoBert?.componentStatus && (
                <Tab label={intl.formatMessage({ id: "event.components.tab" })}>
                  <ComponentStatusTab
                    componentStatus={event.infoBert.componentStatus}
                    eventId={event.id}
                    refreshEvent={refreshEvent}
                  />
                </Tab>
              )}
              {hasAdminPrivileges &&
                event &&
                event.contract &&
                event.contract.deployment &&
                event.contract.deployment.sourceIds.length > 0 && (
                  <Tab label={intl.formatMessage({ id: "event.tab.sources" })}>
                    <DataGridItem sizesOverride={{ xl: 12 }}>
                      <LinkWithIcon
                        text={event.contract.deployment.codename}
                        iconName="settings-input-antenna"
                        color="primary"
                        href={`/deployment/details/${event.contract.deployment.id}`}
                      />
                    </DataGridItem>
                    <br />
                    <EventSourcesTable deploymentId={event.contract.deployment.id} />
                  </Tab>
                )}
              {externalPlayerEnabled && (
                <Tab label={intl.formatMessage({ id: "event.tab.embeddedPlayer" })}>
                  <EmbeddedPlayerTab event={event} />
                </Tab>
              )}
              {(hasSportEventData(event) || showMatchPointConfig(event)) && (
                <Tab label={intl.formatMessage({ id: "event.tab.sportData" })}>
                  {hasSportEventData(event) && <EventSportData event={event} />}
                  {showMatchPointConfig(event) && <MatchPointConfigData event={event} />}
                </Tab>
              )}
              <Tab label={intl.formatMessage({ id: "event.tab.liveStreams" })}>
                <LiveStreamsData event={event} />
              </Tab>
              {hasEmailReceipts(event) && (
                <Tab label={intl.formatMessage({ id: "event.tab.emailReceipts" })}>
                  <EmailReceiptData emails={event.emailReceipts} locales={event.emailLocale} />
                </Tab>
              )}
              {hasGraphicsetAssets(event) && (
                <Tab label={intl.formatMessage({ id: "event.tab.graphicset" })}>
                  <>
                    <DataGridItem sizesOverride={{ xl: 12 }}>
                      <LinkWithIcon
                        text={event.graphicSet.name}
                        iconName="dashboard"
                        color="primary"
                        href={`/graphicset/details/${event.graphicSet.id}/customerId/${event.graphicSet.customerId}`}
                      />
                    </DataGridItem>
                    <br />
                    <EventAssetTable
                      availableSpaces={availableSpaces}
                      eventId={event.id}
                      customerId={event.customerId}
                      graphicSetId={event.graphicSet.id}
                      eventStatus={event.eventStatus}
                    />
                  </>
                </Tab>
              )}
              {(isEventFinished(event) || isEventStarted(event) || isEventDeleted(event)) &&
                user.permissions.includes(permissions.recording_view) && (
                  <Tab label={intl.formatMessage({ id: "event.tab.recordings" })}>
                    <RecordingsData page={parsePage({ queryParams: search })} event={event} />
                  </Tab>
                )}
              {(isEventFinished(event) || isEventStarted(event) || isEventDeleted(event)) &&
                user.permissions.includes(permissions.recording_view) && (
                  <Tab label={intl.formatMessage({ id: "event.tab.clips" })}>
                    <ClipsData clipPage={parsePage({ queryParams: search, paramName: "clipPage" })} event={event} />
                  </Tab>
                )}
              {user.permissions.includes(permissions.recording_view) && event?.localRec && (
                <Tab label={intl.formatMessage({ id: "event.tab.local.recordings" })}>
                  <LocalRecordingsData
                    clipPage={parsePage({ queryParams: search, paramName: "clipPage" })}
                    event={event}
                  />
                </Tab>
              )}
            </Tabs>
          </PageSection>
        </RestrictedEventVisible>
      </QueryResponseHandler>
    </Dashboard>
  );
};
export default EventDetailsPage;
